/**
 * Browser Language Detection Utility
 * 
 * This utility detects the user's browser language and maps it to supported
 * language codes for the menu application. It's used to set the default
 * language when a user first accesses the menu.
 */

// Supported language codes in the application
export const SUPPORTED_LANGUAGES = ['en', 'jp'] as const;
export type SupportedLanguage = typeof SUPPORTED_LANGUAGES[number];

// Language mapping from browser language codes to our supported codes
const LANGUAGE_MAPPING: Record<string, SupportedLanguage> = {
  // Japanese variants
  'ja': 'jp',
  'ja-JP': 'jp',
  'ja-Hira': 'jp',
  'ja-Kana': 'jp',
  'ja-Hani': 'jp',
  
  // English variants
  'en': 'en',
  'en-US': 'en',
  'en-GB': 'en',
  'en-CA': 'en',
  'en-AU': 'en',
  'en-NZ': 'en',
  'en-IE': 'en',
  'en-ZA': 'en',
  'en-JM': 'en',
  'en-BZ': 'en',
  'en-TT': 'en',
  'en-ZW': 'en',
  'en-PH': 'en',
  'en-IN': 'en',
  'en-MY': 'en',
  'en-SG': 'en',
};

/**
 * Detects the browser's preferred language and maps it to a supported language code
 * @returns The detected language code or 'en' as fallback
 */
export function detectBrowserLanguage(): SupportedLanguage {
  // Check if navigator.languages is available (most modern browsers)
  if (navigator.languages && navigator.languages.length > 0) {
    for (const lang of navigator.languages) {
      const mappedLang = LANGUAGE_MAPPING[lang];
      if (mappedLang) {
        return mappedLang;
      }
      
      // Try with just the primary language code (e.g., 'ja' from 'ja-JP')
      const primaryLang = lang.split('-')[0];
      const primaryMappedLang = LANGUAGE_MAPPING[primaryLang];
      if (primaryMappedLang) {
        return primaryMappedLang;
      }
    }
  }
  
  // Fallback to navigator.language (older browsers)
  if (navigator.language) {
    const mappedLang = LANGUAGE_MAPPING[navigator.language];
    if (mappedLang) {
      return mappedLang;
    }
    
    // Try with just the primary language code
    const primaryLang = navigator.language.split('-')[0];
    const primaryMappedLang = LANGUAGE_MAPPING[primaryLang];
    if (primaryMappedLang) {
      return primaryMappedLang;
    }
  }
  
  // Final fallback to English
  return 'en';
}

/**
 * Checks if the detected browser language is Japanese
 * @returns True if the browser language is Japanese
 */
export function isBrowserLanguageJapanese(): boolean {
  return detectBrowserLanguage() === 'jp';
}

/**
 * Gets a human-readable name for the detected browser language
 * @returns The language name (e.g., "Japanese", "English")
 */
export function getBrowserLanguageName(): string {
  const lang = detectBrowserLanguage();
  return lang === 'jp' ? 'Japanese' : 'English';
}
